/**
 * Bundle 114 - Participants tab for Admin Survey Console.
 *
 * Shows the current active campaign's participants (post-repave):
 *   - Header: event, short campaign_id, question_set version hash,
 *     campaign status.
 *   - Send test to <admin email> button (large, on top).
 *   - Send to all N button (label reflects live eligible count).
 *   - Retry-failed button (visible only when there are failed rows).
 *   - Table: name, email (unredacted), variant badge, direct URL
 *     (text field + Copy button + Open new tab), status column
 *     (unsent, sent Xm ago, opened, submitted, failed), status
 *     filter, CSV export.
 *   - Preview-only stub outbox panel (mounted only when the
 *     `/admin/incident/stub-outbox` endpoint returns 200).
 *
 * No em/en dashes, no eslint-disable, no photo/image alt copy.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Copy, ExternalLink, Send, RefreshCw, Download, MailCheck,
  AlertTriangle, TestTube2, ShieldCheck, Wand2, Eraser, Mail,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABEL = {
  unsent: 'Unsent',
  sent: 'Sent',
  opened: 'Opened',
  submitted: 'Submitted',
  failed: 'Failed',
};

const STATUS_TONE = {
  unsent: 'bg-neutral-100 text-neutral-700 border-neutral-200',
  sent: 'bg-sky-50 text-sky-800 border-sky-200',
  opened: 'bg-amber-50 text-amber-800 border-amber-200',
  submitted: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  failed: 'bg-rose-50 text-rose-800 border-rose-200',
};

function statusText(row) {
  if (row.status === 'sent' && typeof row.sent_ago_minutes === 'number') {
    if (row.sent_ago_minutes < 1) return 'Sent just now';
    if (row.sent_ago_minutes < 60) return `Sent ${row.sent_ago_minutes}m ago`;
    const hrs = Math.floor(row.sent_ago_minutes / 60);
    return `Sent ${hrs}h ago`;
  }
  return STATUS_LABEL[row.status] || row.status;
}

export function ParticipantsTab({ eventId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [testResult, setTestResult] = useState(null);
  const [adminEmail, setAdminEmail] = useState('');
  const [confirmSendAll, setConfirmSendAll] = useState(false);
  const [sendAllResult, setSendAllResult] = useState(null);
  const [stubOutbox, setStubOutbox] = useState(null);
  const [stubEnabled, setStubEnabled] = useState(false);
  const [repaveDryRun, setRepaveDryRun] = useState(null);
  const [showRepaveDialog, setShowRepaveDialog] = useState(false);
  const [refreshDryRun, setRefreshDryRun] = useState(null);
  const [showRefreshDialog, setShowRefreshDialog] = useState(false);
  const [resetRow, setResetRow] = useState(null);
  const [resetDryRun, setResetDryRun] = useState(null);
  // Bundle 179 - Draft campaigns have 0 invitations by construction
  // so the participants payload reads 0/0/0. This confused ops on
  // the Philly campaign (they thought Force Check In and Survey
  // were both broken). Surface the eligibility preview count in a
  // dedicated draft-state callout.
  const [previewData, setPreviewData] = useState(null);
  const CURRENT_BUNDLE = 116;

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [me, participants, preview] = await Promise.all([
        api.get('/auth/me').catch(() => ({ data: null })),
        api.get(`/admin/events/${eventId}/survey/participants`),
        // Bundle 179 - fetch the recipients-preview so the tab can
        // surface an eligible count for DRAFT campaigns (no
        // invitations minted yet -> participants=0/0/0, which
        // looks broken but is correct).
        api.get(
          `/admin/events/${eventId}/survey/recipients-preview`,
        ).catch(() => ({ data: null })),
      ]);
      setAdminEmail(me?.data?.user?.email || me?.data?.email || '');
      setData(participants.data);
      setPreviewData(preview.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail
                || 'Failed to load participants');
    } finally {
      setBusy(false);
    }
  }, [eventId]);

  const loadStubOutbox = useCallback(async () => {
    try {
      const { data: box } = await api.get(
        '/admin/incident/stub-outbox',
      );
      setStubOutbox(box);
      setStubEnabled(true);
    } catch (e) {
      // 404 means production (no stub). Silent.
      setStubEnabled(false);
    }
  }, []);

  useEffect(() => { load(); loadStubOutbox(); }, [load, loadStubOutbox]);

  const rows = useMemo(() => {
    const all = data?.rows || [];
    if (statusFilter === 'all') return all;
    return all.filter((r) => r.status === statusFilter);
  }, [data, statusFilter]);

  const testFreshMinutes = useMemo(() => {
    const t = data?.last_send_test_to_admin_at;
    if (!t) return null;
    try {
      const dt = new Date(t);
      return Math.round((Date.now() - dt.getTime()) / 60000);
    } catch {
      return null;
    }
  }, [data]);

  const sendAllEnabled = (
    testFreshMinutes !== null && testFreshMinutes <= 10
  );

  const eligibleCount = data?.counts?.total ?? 0;
  const failedCount = data?.counts?.failed ?? 0;

  const doRepaveDryRun = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post(
        '/admin/incident/repave-campaign',
        { event_id: eventId, dry_run: true },
      );
      setRepaveDryRun(d);
      setShowRepaveDialog(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
                || e?.response?.data?.detail
                || 'Repave dry-run failed');
    } finally { setBusy(false); }
  };

  const doRepaveApply = async () => {
    if (!window.confirm(
      'Apply repave now? This archives the current active campaign '
      + `and mints ${repaveDryRun?.would_mint || '?'} fresh `
      + 'invitations. No emails will be sent.',
    )) return;
    setBusy(true);
    try {
      const { data: d } = await api.post(
        '/admin/incident/repave-campaign',
        { event_id: eventId, dry_run: false },
      );
      toast.success(`Repave applied. Minted ${d.minted} invitations.`);
      setShowRepaveDialog(false);
      setRepaveDryRun(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
                || e?.response?.data?.detail
                || 'Repave apply failed');
    } finally { setBusy(false); }
  };

  const doRefreshDryRun = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post(
        '/admin/incident/refresh-question-set',
        { event_id: eventId, dry_run: true },
      );
      setRefreshDryRun(d);
      setShowRefreshDialog(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
                || e?.response?.data?.detail
                || 'Refresh dry-run failed');
    } finally { setBusy(false); }
  };

  const doRefreshApply = async () => {
    if (!window.confirm(
      'Apply question-set refresh now? This updates the schema on '
      + `campaign ${refreshDryRun?.campaign_id_short || '?'} in place. `
      + 'Invitations, tokens, URLs, and responses are NOT touched. '
      + 'No emails will be sent.',
    )) return;
    setBusy(true);
    try {
      const { data: d } = await api.post(
        '/admin/incident/refresh-question-set',
        { event_id: eventId, dry_run: false },
      );
      if (d.refreshed) {
        toast.success(
          `Question set refreshed. ${d.diff_count} rule change(s) `
          + `applied. ${d.invitations_preserved} invitations + `
          + `${d.responses_preserved} responses preserved.`,
        );
      } else {
        toast.success(
          'Question set already at current bundle. No change.',
        );
      }
      setShowRefreshDialog(false);
      setRefreshDryRun(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
                || e?.response?.data?.detail
                || 'Refresh apply failed');
    } finally { setBusy(false); }
  };

  const refreshVisible = (
    (data?.counts?.total || 0) > 0
    && (data?.campaign?.question_set_refreshed_bundle
        || 0) < CURRENT_BUNDLE
  );

  const openResetDialog = async (row) => {
    if (!row?.registration_id) {
      toast.error('No registration_id on this row.');
      return;
    }
    setResetRow(row);
    setResetDryRun(null);
    setBusy(true);
    try {
      const { data: d } = await api.post(
        '/admin/incident/reset-registration-survey-state',
        {
          event_id: eventId,
          registration_id: row.registration_id,
          dry_run: true,
        },
      );
      setResetDryRun(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
                || e?.response?.data?.detail
                || 'Reset dry-run failed');
      setResetRow(null);
    } finally { setBusy(false); }
  };

  const applyReset = async () => {
    if (!resetRow?.registration_id) return;
    setBusy(true);
    try {
      const { data: d } = await api.post(
        '/admin/incident/reset-registration-survey-state',
        {
          event_id: eventId,
          registration_id: resetRow.registration_id,
          dry_run: false,
        },
      );
      toast.success(
        `Reset complete. Deleted ${d.deleted_responses} response(s). `
        + `Cleared ${d.reset_fields.length} field(s). Invitation `
        + 'preserved.',
      );
      setResetRow(null);
      setResetDryRun(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
                || e?.response?.data?.detail
                || 'Reset apply failed');
    } finally { setBusy(false); }
  };

  const sendTestToMe = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const { data: d } = await api.post(
        `/admin/events/${eventId}/survey/participants/send-test-to-me`,
      );
      setTestResult(d);
      if (d.sent) {
        toast.success(`Test sent to ${d.to}. Verify the email + link.`);
      } else {
        toast.error(`Send failed: ${d.reason || 'unknown'}`);
      }
      await load();
      await loadStubOutbox();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
                || e?.response?.data?.detail
                || 'Send test failed');
    } finally { setBusy(false); }
  };

  const sendAll = async () => {
    setConfirmSendAll(false);
    setBusy(true);
    try {
      const { data: d } = await api.post(
        `/admin/events/${eventId}/survey/participants/send-all`,
      );
      setSendAllResult(d);
      if (d.failed > 0) {
        toast.error(`Sent ${d.sent}, failed ${d.failed}. Review below.`);
      } else {
        toast.success(`Sent to all ${d.sent} recipients.`);
      }
      await load();
      await loadStubOutbox();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
                || e?.response?.data?.detail
                || 'Send all failed');
    } finally { setBusy(false); }
  };

  const retryFailed = async () => {
    if (!window.confirm(`Retry ${failedCount} failed sends?`)) return;
    setBusy(true);
    try {
      const { data: d } = await api.post(
        `/admin/events/${eventId}/survey/participants/retry-failed`,
      );
      setSendAllResult(d);
      toast.success(`Retried ${d.attempted}. Sent ${d.sent}, `
                  + `failed ${d.failed}.`);
      await load();
      await loadStubOutbox();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
                || e?.response?.data?.detail
                || 'Retry failed');
    } finally { setBusy(false); }
  };

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL copied');
    } catch {
      toast.error('Clipboard blocked. Select the field manually.');
    }
  };

  const downloadCsv = () => {
    const header = ['name', 'email', 'variant', 'status',
                     'sent_at', 'url'];
    const csv = [header.join(',')].concat(
      (data?.rows || []).map((r) => header.map((k) => {
        const v = r[k] == null ? '' : String(r[k]);
        return '"' + v.replaceAll('"', '""') + '"';
      }).join(',')),
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `participants-${eventId}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const testToLabel = adminEmail || 'yaar@dostination.com';

  return (
    <div className="space-y-4" data-testid="participants-tab">
      {/* Header */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="flex flex-col gap-1">
              <div className="text-[10px] uppercase tracking-widest
                                    text-neutral-500">
                Active campaign
              </div>
              <div className="flex flex-wrap gap-2 items-center text-sm">
                <span className="font-mono text-primary-700"
                            data-testid="participants-campaign-id">
                  {data?.campaign?.id_short || 'no-campaign'}
                </span>
                <Badge variant="outline"
                            data-testid="participants-campaign-status">
                  {data?.campaign?.status || 'no-campaign'}
                </Badge>
                <span className="text-neutral-500">
                  qset
                </span>
                <span className="font-mono text-xs text-neutral-600"
                            data-testid="participants-qset-hash">
                  {data?.question_set_version_hash || 'na'}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline"
                            onClick={doRepaveDryRun}
                            disabled={busy}
                            data-testid="participants-repave-dryrun-btn">
                <RefreshCw className="w-4 h-4 mr-1" />
                Repave dry-run
              </Button>
              {refreshVisible ? (
                <Button size="sm" variant="outline"
                              onClick={doRefreshDryRun}
                              disabled={busy}
                              data-testid="participants-refresh-qset-btn"
                              className="border-primary-300
                                              text-primary-800
                                              hover:bg-primary-50">
                  <Wand2 className="w-4 h-4 mr-1" />
                  Refresh question set
                </Button>
              ) : null}
              <Button size="sm" variant="outline"
                            onClick={load} disabled={busy}
                            data-testid="participants-reload-btn">
                Refresh
              </Button>
              <Button size="sm" variant="outline"
                            onClick={downloadCsv}
                            data-testid="participants-csv-btn">
                <Download className="w-4 h-4 mr-1" />
                CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bundle 179 - Draft campaign callout. Draft campaigns have
          0 invitations minted by construction, so the participant
          counts read 0/0/0. This looks broken (see Bundle 178 Philly
          incident). Surface the eligibility preview so the operator
          understands what will happen on Send Live. */}
      {data?.campaign?.status === 'draft' ? (
        <Card>
          <CardContent
            className="p-4"
            data-testid="participants-draft-campaign-callout"
          >
            <div className="flex items-start gap-3">
              <div className="rounded-md bg-amber-100 p-2">
                <AlertTriangle className="w-4 h-4 text-amber-800" />
              </div>
              <div className="text-sm">
                <div className="font-semibold text-neutral-900">
                  Campaign is in draft. No invitations minted yet.
                </div>
                <div className="text-neutral-700 mt-1">
                  {previewData ? (
                    <>
                      <span
                        data-testid="participants-draft-eligible-count"
                        className="font-semibold"
                      >
                        {previewData?.counts?.eligible ?? 0}
                      </span>{' '}
                      guests will receive invitations when you go
                      live ({previewData?.counts?.attendee ?? 0}{' '}
                      attendee variant,{' '}
                      {previewData?.counts?.non_attendee ?? 0}{' '}
                      non-attendee variant).{' '}
                      {previewData?.counts?.excluded > 0 ? (
                        <span className="text-neutral-500">
                          {previewData.counts.excluded} excluded
                          (see Overview for reasons).
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <>Loading eligibility preview...</>
                  )}
                </div>
                <div className="text-xs text-neutral-500 mt-1">
                  Once you Send Live, this tab populates with
                  per-invitation delivery state.
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Send test to me */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start gap-3 flex-wrap">
            <Button
              size="lg"
              onClick={sendTestToMe}
              disabled={busy || !data?.campaign?.id}
              data-testid="participants-send-test-btn"
              className="bg-primary-800 text-white hover:bg-primary-900"
            >
              <TestTube2 className="w-4 h-4 mr-2" />
              Send test to {testToLabel}
            </Button>
            <div className="flex-1 min-w-[220px] text-xs text-neutral-600">
              Uses registration[0] deterministically for interpolation.
              Sends through the real send_email path. The Send all
              button unlocks after a successful test within the last
              10 minutes.
            </div>
          </div>
          {testResult ? (
            <div
              className={
                'rounded-md p-3 text-sm border '
                + (testResult.sent
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  : 'bg-rose-50 border-rose-200 text-rose-900')
              }
              data-testid="participants-send-test-banner"
            >
              {testResult.sent ? (
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" />
                  <span>
                    Test sent to <strong>{testResult.to}</strong>
                    {(testResult.stubbed_bundle_114
                      && data?.stub_transport_enabled)
                      ? ' (preview stub transport - no real inbox)'
                      : ''}.
                    Verify the email arrived and the survey link opens
                    correctly. Send all is now enabled for 10 minutes.
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span>
                    Send failed: <code>{testResult.reason}</code>
                  </span>
                </div>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Counts + Send all */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4 justify-between">
            <div className="flex flex-wrap gap-3 text-sm">
              <span data-testid="participants-count-total">
                <strong>{data?.counts?.total || 0}</strong> total
              </span>
              <span>
                <strong>{data?.counts?.attendee || 0}</strong> attendee
              </span>
              <span>
                <strong>{data?.counts?.non_attendee || 0}</strong>
                {' '}non-attendee
              </span>
              <span className="text-emerald-800">
                <strong>{data?.counts?.sent || 0}</strong> sent
              </span>
              <span className="text-neutral-700">
                <strong>{data?.counts?.unsent || 0}</strong> unsent
              </span>
              <span className="text-rose-800">
                <strong>{failedCount}</strong> failed
              </span>
            </div>
            <div className="flex gap-2">
              {failedCount > 0 ? (
                <Button size="sm" variant="outline"
                              onClick={retryFailed}
                              disabled={busy}
                              data-testid="participants-retry-failed-btn">
                  Retry {failedCount} failed
                </Button>
              ) : null}
              <Button
                size="lg"
                onClick={() => setConfirmSendAll(true)}
                disabled={busy || !sendAllEnabled || eligibleCount === 0}
                data-testid="participants-send-all-btn"
                className="bg-emerald-700 text-white hover:bg-emerald-800
                                disabled:bg-neutral-300"
              >
                <Send className="w-4 h-4 mr-2" />
                Send to all {eligibleCount}
              </Button>
            </div>
          </div>
          {!sendAllEnabled ? (
            <div className="mt-2 text-xs text-neutral-600">
              Send test to me first. Send all unlocks for 10 minutes
              after each successful test send.
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Send all result */}
      {sendAllResult ? (
        <Card>
          <CardContent className="p-4 space-y-2">
            <div className="text-sm">
              <strong>Send all result:</strong>{' '}
              {sendAllResult.sent} sent, {sendAllResult.failed} failed
              (attempted {sendAllResult.attempted}).
            </div>
            {sendAllResult.first_error ? (
              <div
                className="rounded-md p-3 text-sm border bg-rose-50
                                border-rose-200 text-rose-900"
                data-testid="participants-send-all-error"
              >
                First error: <code>{sendAllResult.first_error}</code>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {/* Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="text-[10px] uppercase tracking-widest
                                text-neutral-500">
              Filter
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40"
                                data-testid="participants-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="unsent">Unsent</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="opened">Opened</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-xs text-neutral-500">
              Showing {rows.length} of {data?.counts?.total || 0}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs"
                        data-testid="participants-table">
              <thead>
                <tr className="text-left text-neutral-500">
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Email</th>
                  <th className="py-2 pr-3">Variant</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3" data-testid="participants-col-last-reminder">
                    Last reminder
                  </th>
                  <th className="py-2 pr-3">Direct URL</th>
                  <th className="py-2 pr-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.invitation_id}
                    className="border-t border-neutral-100 align-top"
                    data-testid={`participant-row-${r.invitation_id}`}
                  >
                    <td className="py-2 pr-3">{r.name}</td>
                    <td className="py-2 pr-3 font-mono text-[11px]">
                      {r.email}
                    </td>
                    <td className="py-2 pr-3">
                      <Badge variant="outline">
                        {r.variant}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3">
                      <span
                        className={
                          'inline-block px-2 py-0.5 rounded border '
                          + (STATUS_TONE[r.status]
                            || STATUS_TONE.unsent)
                        }
                      >
                        {statusText(r)}
                      </span>
                      {r.send_error ? (
                        <div
                          className="text-[10px] text-rose-700 mt-1
                                          font-mono max-w-[240px] truncate"
                          title={r.send_error}
                        >
                          {r.send_error}
                        </div>
                      ) : null}
                    </td>
                    <td className="py-2 pr-3"
                        data-testid={`participant-reminder-${r.invitation_id}`}>
                      {r.reminder_sent_at ? (
                        <span
                          className="inline-flex items-center gap-1
                                     text-[10px] px-2 py-0.5 rounded
                                     border bg-blue-50 text-blue-800
                                     border-blue-200"
                          title={r.reminder_sent_at}
                        >
                          <Mail className="w-3 h-3" />
                          {String(r.reminder_sent_at).slice(0, 10)}
                        </span>
                      ) : (
                        <span className="text-neutral-400">-</span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2">
                        <Input
                          value={r.url}
                          readOnly
                          className="text-[11px] font-mono h-7
                                          min-w-[240px]"
                          data-testid={
                            `participant-url-${r.invitation_id}`
                          }
                          onFocus={(e) => e.target.select()}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyUrl(r.url)}
                          data-testid={
                            `participant-copy-${r.invitation_id}`
                          }
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                        <a
                          href={r.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary-700 hover:underline
                                          text-[11px]"
                          data-testid={
                            `participant-open-${r.invitation_id}`
                          }
                        >
                          <ExternalLink className="w-3 h-3
                                                        inline-block" />
                        </a>
                      </div>
                    </td>
                    <td className="py-2 pr-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openResetDialog(r)}
                        className="border-rose-300 text-rose-800
                                        hover:bg-rose-50"
                        data-testid={
                          `participant-reset-${r.invitation_id}`
                        }
                      >
                        <Eraser className="w-3 h-3 mr-1" />
                        Reset state
                      </Button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={7}
                            className="text-center text-neutral-500 py-6">
                      No participants match the filter. Run repave
                      dry-run then apply to mint fresh invitations.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Preview stub outbox (only when the backend confirms stub is
          active). Belt-and-suspenders: the endpoint 404s in
          production, AND the participants payload carries a
          `stub_transport_enabled` boolean that gates the panel. */}
      {stubEnabled && stubOutbox && data?.stub_transport_enabled ? (
        <Card>
          <CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-widest
                                text-secondary-800 mb-2">
              Preview stub outbox ({stubOutbox.count} of last 20)
            </div>
            <div className="text-xs text-neutral-600 mb-3"
                        data-testid="stub-outbox-note">
              {stubOutbox.note}
            </div>
            <div className="space-y-2">
              {stubOutbox.rows.map((row, idx) => (
                <div
                  key={row.id || idx}
                  className="border border-neutral-200 rounded p-3 text-xs"
                  data-testid={`stub-outbox-row-${idx}`}
                >
                  <div className="flex justify-between mb-1">
                    <div className="font-mono">
                      <strong>{row.template_id}</strong>{' -&gt; '}
                      {row.to}
                    </div>
                    <div className="text-neutral-500">
                      {row.created_at}
                    </div>
                  </div>
                  <div className="text-neutral-700">
                    {row.subject}
                  </div>
                </div>
              ))}
              {stubOutbox.rows.length === 0 ? (
                <div className="text-neutral-500 text-xs">
                  Empty. Send test to me or send all to populate.
                </div>
              ) : null}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Confirm Send All */}
      <Dialog open={confirmSendAll} onOpenChange={setConfirmSendAll}>
        <DialogContent data-testid="send-all-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Send to all {eligibleCount} recipients?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <div>
              This will dispatch a fresh survey invitation to
              every currently pending recipient.
            </div>
            <ul className="text-xs text-neutral-700 list-disc pl-5">
              <li>
                Attendee variant: {data?.counts?.attendee || 0}
              </li>
              <li>
                Non-attendee variant: {data?.counts?.non_attendee || 0}
              </li>
              <li>
                Serial dispatch with 250ms spacing. Hard-stop on
                first error.
              </li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline"
                          onClick={() => setConfirmSendAll(false)}>
              Cancel
            </Button>
            <Button onClick={sendAll} disabled={busy}
                          data-testid="send-all-confirm-btn"
                          className="bg-emerald-700 text-white
                                          hover:bg-emerald-800">
              <MailCheck className="w-4 h-4 mr-2" />
              Yes, send all {eligibleCount}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Repave dry-run dialog */}
      <Dialog open={showRepaveDialog}
                    onOpenChange={setShowRepaveDialog}>
        <DialogContent data-testid="repave-dialog"
                            className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Repave campaign dry-run</DialogTitle>
          </DialogHeader>
          {repaveDryRun ? (
            <div className="space-y-3 text-sm">
              <div>
                Would archive campaign{' '}
                <code className="font-mono">
                  {repaveDryRun.current_active_campaign_id_short
                   || 'none'}
                </code>{' '}
                and mint <strong>{repaveDryRun.would_mint}</strong>{' '}
                fresh invitations on a new campaign
                {' '}<code className="font-mono">
                  {repaveDryRun.would_new_campaign_id_short}
                </code>.
              </div>
              <div>
                Attendee: {repaveDryRun.counts.attendee}. Non-attendee:{' '}
                {repaveDryRun.counts.non_attendee}. Excluded:{' '}
                {repaveDryRun.counts.excluded}.
              </div>
              {Object.entries(repaveDryRun.skipped_by_reason || {}).length
                > 0 ? (
                  <div className="text-xs">
                    Skipped:{' '}
                    {Object.entries(repaveDryRun.skipped_by_reason)
                      .map(([k, v]) => `${k}=${v}`).join(', ')}
                  </div>
                ) : null}
              <div className="max-h-64 overflow-y-auto border rounded">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-neutral-50 text-left">
                      <th className="p-2">Name</th>
                      <th className="p-2">Email</th>
                      <th className="p-2">Variant</th>
                      <th className="p-2">URL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(repaveDryRun.survey_urls || []).map((r) => (
                      <tr key={r.invitation_id}
                              className="border-t border-neutral-100">
                        <td className="p-2">{r.name}</td>
                        <td className="p-2 font-mono">{r.email}</td>
                        <td className="p-2">{r.variant}</td>
                        <td className="p-2 font-mono text-[10px]
                                            max-w-[260px] truncate">
                          {r.url}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline"
                          onClick={() => setShowRepaveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={doRepaveApply} disabled={busy}
                          data-testid="repave-apply-btn"
                          className="bg-primary-800 text-white
                                          hover:bg-primary-900">
              Apply repave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refresh question set dialog */}
      <Dialog open={showRefreshDialog}
                    onOpenChange={setShowRefreshDialog}>
        <DialogContent data-testid="refresh-qset-dialog"
                            className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Refresh question set in place</DialogTitle>
          </DialogHeader>
          {refreshDryRun ? (
            <div className="space-y-3 text-sm">
              <div className="rounded-md bg-primary-50
                                    border border-primary-200 p-3
                                    text-primary-900 text-xs">
                This freshens the schema on campaign{' '}
                <code className="font-mono">
                  {refreshDryRun.campaign_id_short}
                </code>{' '}
                without archiving anything. Invitations, tokens,
                URLs, and responses are preserved. Use this when a
                validation-rule bundle ships and you do NOT want to
                invalidate tokens already tracked in the wild.
              </div>
              <div className="flex flex-wrap gap-4 text-xs">
                <div>
                  Diff:{' '}
                  <strong data-testid="refresh-qset-diff-count">
                    {refreshDryRun.diff_count}
                  </strong>{' '}
                  question(s)
                </div>
                <div>
                  Invitations preserved:{' '}
                  <strong
                    data-testid="refresh-qset-invitations-preserved"
                  >
                    {refreshDryRun.invitations_preserved}
                  </strong>
                </div>
                <div>
                  Responses preserved:{' '}
                  <strong
                    data-testid="refresh-qset-responses-preserved"
                  >
                    {refreshDryRun.responses_preserved}
                  </strong>
                </div>
                <div>
                  Old hash:{' '}
                  <code className="font-mono">
                    {refreshDryRun.old_question_set_version_hash}
                  </code>
                </div>
                <div>
                  New hash:{' '}
                  <code className="font-mono">
                    {refreshDryRun.new_question_set_version_hash}
                  </code>
                </div>
              </div>
              <div className="max-h-72 overflow-y-auto border rounded"
                          data-testid="refresh-qset-diff-table">
                {refreshDryRun.diff_count === 0 ? (
                  <div className="p-4 text-neutral-600 text-xs">
                    Already at current bundle. No changes to apply.
                  </div>
                ) : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-neutral-50 text-left">
                        <th className="p-2">Question</th>
                        <th className="p-2">Change</th>
                        <th className="p-2">Old rules</th>
                        <th className="p-2">New rules</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(refreshDryRun.diff || []).map((d) => (
                        <tr
                          key={d.question_key}
                          className="border-t border-neutral-100
                                          align-top"
                          data-testid={
                            `refresh-qset-diff-row-${d.question_key}`
                          }
                        >
                          <td className="p-2 font-mono">
                            {d.question_key}
                          </td>
                          <td className="p-2">
                            <Badge variant="outline">
                              {d.change}
                            </Badge>
                          </td>
                          <td className="p-2 font-mono
                                              text-[10px] max-w-[240px]
                                              whitespace-pre-wrap
                                              break-all">
                            {d.old_rules
                              ? JSON.stringify(d.old_rules, null, 1)
                              : '(none)'}
                          </td>
                          <td className="p-2 font-mono
                                              text-[10px] max-w-[240px]
                                              whitespace-pre-wrap
                                              break-all">
                            {d.new_rules
                              ? JSON.stringify(d.new_rules, null, 1)
                              : '(removed)'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline"
                          onClick={() => setShowRefreshDialog(false)}>
              Cancel
            </Button>
            <Button onClick={doRefreshApply} disabled={busy}
                          data-testid="refresh-qset-apply-btn"
                          className="bg-primary-800 text-white
                                          hover:bg-primary-900">
              Apply refresh
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset registration state dialog */}
      <Dialog open={!!resetRow}
                    onOpenChange={(v) => { if (!v) setResetRow(null); }}>
        <DialogContent data-testid="reset-state-dialog"
                            className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Reset survey state for {resetRow?.name || 'this row'}
            </DialogTitle>
          </DialogHeader>
          {resetDryRun ? (
            <div className="space-y-3 text-sm">
              <div className="rounded-md bg-rose-50 border
                                    border-rose-200 p-3 text-rose-900 text-xs">
                Bundle 117 - one-click recovery. Removes any autosaved
                or submitted survey responses under this registration
                and clears the invitation open-state markers.
                Invitation id + token are preserved.
              </div>
              <div className="flex flex-wrap gap-4 text-xs">
                <div>
                  Responses to delete:{' '}
                  <strong data-testid="reset-state-delete-count">
                    {resetDryRun.would_delete_responses}
                  </strong>
                </div>
                <div>
                  Fields to clear:{' '}
                  <strong data-testid="reset-state-fields-count">
                    {resetDryRun.would_reset_fields?.length || 0}
                  </strong>{' '}
                  ({(resetDryRun.would_reset_fields || []).join(', ')
                    || 'none'})
                </div>
                <div>
                  Invitation id preserved:{' '}
                  <code className="font-mono">
                    {resetDryRun.invitation_id_short || 'n/a'}
                  </code>
                </div>
              </div>
              {resetDryRun.responses_snapshot?.length > 0 ? (
                <div className="text-xs text-neutral-700
                                      border rounded p-2">
                  <div className="font-semibold mb-1">
                    Responses that will be deleted:
                  </div>
                  <ul className="list-disc pl-5">
                    {resetDryRun.responses_snapshot.map((rr) => (
                      <li key={rr.id}>
                        <code className="font-mono">
                          {rr.id?.slice(0, 8)}
                        </code>{' '}
                        (submitted:{' '}
                        {rr.submitted_at ? 'yes' : 'no'}, campaign:{' '}
                        <code className="font-mono">
                          {rr.campaign_id_short}
                        </code>)
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="text-sm text-neutral-500">
              Loading dry-run...
            </div>
          )}
          <DialogFooter>
            <Button variant="outline"
                          onClick={() => setResetRow(null)}
                          data-testid="reset-state-cancel-btn">
              Cancel
            </Button>
            <Button onClick={applyReset} disabled={busy || !resetDryRun}
                          data-testid="reset-state-apply-btn"
                          className="bg-rose-700 text-white
                                          hover:bg-rose-800">
              Apply reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ParticipantsTab;
